import { useAuthStore, User } from '@/stores';
import { useMemo, useCallback } from 'react';

export const CANONICAL_PERMISSIONS = [
  'tickets.read',
  'tickets.create',
  'tickets.reply',
  'tickets.manage',
  'customers.read',
  'customers.create',
  'customers.update',
  'subscriptions.read',
  'subscriptions.manage',
  'wallet.read',
  'wallet.transfer',
  'debts.manage',
  'tasks.read',
  'tasks.manage',
  'requests.read',
  'requests.manage',
  'settings.read',
  'settings.manage',
  'users.manage',
] as const;

export type CanonicalPermission = typeof CANONICAL_PERMISSIONS[number];

// Default baseline role permissions mapping (aligned with backend migrations & seeds)
export const ROLE_DEFAULT_PERMISSIONS: Record<string, CanonicalPermission[]> = {
  super_admin: [...CANONICAL_PERMISSIONS],
  contractor_admin: [...CANONICAL_PERMISSIONS],
  support_agent: [
    'tickets.read',
    'tickets.create',
    'tickets.reply',
    'tickets.manage',
    'customers.read',
    'customers.create',
    'customers.update',
    'subscriptions.read',
    'subscriptions.manage',
    'wallet.read',
    'tasks.read',
    'requests.read',
    'requests.manage',
    'settings.read',
  ],
  field_tech: [
    'tickets.read',
    'tickets.reply',
    'customers.read',
    'subscriptions.read',
    'tasks.read',
    'tasks.manage',
    'requests.read',
  ],
  viewer: [
    'tickets.read',
    'customers.read',
    'subscriptions.read',
    'wallet.read',
    'tasks.read',
    'requests.read',
    'settings.read',
  ],
};

/**
 * Resolves the effective list of canonical permissions for a user.
 * 
 * Rules:
 * 1. Super Admin: possesses all canonical permissions.
 * 2. SSO Owner (authType === 'SSO' && isOwner === true): possesses all canonical permissions.
 * 3. Local Sub-User: strictly consumes backend-provided `user.permissions` array.
 *    - If permissions is [] -> returns [] (fail-closed).
 *    - If permissions is undefined/null -> returns [] (fail-closed, no role fallback).
 */
export function resolveEffectivePermissions(user: User | null): string[] {
  if (!user) return [];

  // Super Admin platform-level bypass: possesses all canonical permissions
  const isSuper = Boolean(
    user.roles?.includes('super_admin') ||
    user.role === 'super_admin' ||
    (user as any).authType === 'SUPER_ADMIN'
  );
  if (isSuper) {
    return [...CANONICAL_PERMISSIONS];
  }

  // SSO Owner (Contractor Account Owner via Keycloak SSO)
  const isSsoOwner = Boolean(user.authType === 'SSO' && user.isOwner);
  if (isSsoOwner) {
    return [...CANONICAL_PERMISSIONS];
  }

  // Explicit backend permissions array (e.g. from /api/auth/sub-user-login or /api/auth/me)
  if (Array.isArray(user.permissions)) {
    return user.permissions;
  }

  // Fail-closed: No authoritative permissions provided by backend
  return [];
}

export interface UsePermissionReturn {
  hasPermission: (permission: CanonicalPermission | string) => boolean;
  hasAnyPermission: (permissions: (CanonicalPermission | string)[]) => boolean;
  hasAllPermissions: (permissions: (CanonicalPermission | string)[]) => boolean;
  isSuperAdmin: boolean;
  isOwner: boolean;
  effectivePermissions: string[];
  permissions: Record<CanonicalPermission, boolean>;
  roles: string[];
  role?: string;
  isAuthenticated: boolean;
}

/**
 * Central permission hook for FTTH Nexus frontend.
 * Provides granular capability checking based on the authenticated user's effective permissions.
 */
export function usePermission(): UsePermissionReturn {
  const storeUser = useAuthStore((s) => s.user);
  const user = storeUser ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().user : null);
  const storeAuth = useAuthStore((s) => s.isAuthenticated);
  const isAuthenticated = storeAuth ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().isAuthenticated : false);

  const isSuperAdmin = useMemo(() => {
    if (!user) return false;
    return Boolean(
      user.roles?.includes('super_admin') ||
      user.role === 'super_admin' ||
      (user as any).authType === 'SUPER_ADMIN'
    );
  }, [user]);

  const isOwner = useMemo(() => {
    if (!user) return false;
    return Boolean(user.authType === 'SSO' && user.isOwner);
  }, [user]);

  const effectivePermissions = useMemo(() => {
    return resolveEffectivePermissions(user);
  }, [user]);

  const permissionsSet = useMemo(() => {
    return new Set<string>(effectivePermissions);
  }, [effectivePermissions]);

  const hasPermission = useCallback(
    (permission: CanonicalPermission | string): boolean => {
      if (!user) return false;
      if (isSuperAdmin || isOwner) return true;
      return permissionsSet.has(permission);
    },
    [user, isSuperAdmin, isOwner, permissionsSet]
  );

  const hasAnyPermission = useCallback(
    (permissions: (CanonicalPermission | string)[]): boolean => {
      if (!user) return false;
      if (isSuperAdmin || isOwner) return true;
      if (!permissions || permissions.length === 0) return true;
      return permissions.some((p) => permissionsSet.has(p));
    },
    [user, isSuperAdmin, isOwner, permissionsSet]
  );

  const hasAllPermissions = useCallback(
    (permissions: (CanonicalPermission | string)[]): boolean => {
      if (!user) return false;
      if (isSuperAdmin || isOwner) return true;
      if (!permissions || permissions.length === 0) return true;
      return permissions.every((p) => permissionsSet.has(p));
    },
    [user, isSuperAdmin, isOwner, permissionsSet]
  );

  const permissionsMap = useMemo(() => {
    const map = {} as Record<CanonicalPermission, boolean>;
    for (const p of CANONICAL_PERMISSIONS) {
      map[p] = isSuperAdmin || permissionsSet.has(p);
    }
    return map;
  }, [isSuperAdmin, permissionsSet]);

  const roles = user?.roles || (user?.role ? [user.role] : []);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    isOwner,
    effectivePermissions,
    permissions: permissionsMap,
    roles,
    role: user?.role || roles[0],
    isAuthenticated,
  };
}
